import { Badge } from "@/components/ui/baseUi/badge"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import type { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import { word } from "@/modules/core/utils/i18n"
import { formatCurrency } from "@/modules/currency/currency"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import emptyShopItemImage from "@images/boxShop/empty-shop-item-image.webp"

type BoxShopCardItemProps = {
  item: BoxShopItemEntity
  onAddItem?: (item: BoxShopItemEntity) => void
  className?: string
} & React.ComponentProps<"div">

export function BoxShopCardItem({ item, onAddItem, className, ...props }: BoxShopCardItemProps) {
  const language = useLanguage()
  return (
    <div
      className={cn(
        "flex h-45 w-40 cursor-pointer flex-col overflow-hidden rounded-2xl border border-interactive-border-neutral-default hover:border-interactive-border-neutral-highlighted hover:bg-interactive-background-neutral-highlighted",
        className,
      )}
      {...props}
      onClick={() => onAddItem?.(item)}
    >
      <div className="relative">
        {item.image?.url ? (
          <img src={item.image.url} alt={item.title} className="h-22 w-full object-cover" />
        ) : (
          <img src={emptyShopItemImage} alt={item.title} className="h-22 w-full object-cover" />
        )}
        <Badge variant="transparent" size="sm" className="absolute right-2 bottom-2">
          {item.stock} {word("boxShop.cardItem.stock.left", { count: item.stock })}
        </Badge>
      </div>
      <div className="flex flex-1 flex-col justify-between px-2 pt-1 pb-2">
        <div className="flex flex-col">
          {item.title.length > 47 ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Text fontStyle="LM" className="line-clamp-2">
                    {item.title}
                  </Text>
                }
              />
              <TooltipContent side="bottom" className="w-fit max-w-60 wrap-break-word whitespace-normal">
                {item.title}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Text fontStyle="LM" className="line-clamp-2">
              {item.title}
            </Text>
          )}
          <Text fontStyle="LXS" className="truncate">
            {item.brand}
          </Text>
        </div>
        <Text fontStyle="TS" className="text-right">
          {formatCurrency(item.price.value, item.price.currency, language)}
        </Text>
      </div>
    </div>
  )
}
